import { useCallback, useEffect, useRef, useState } from 'react';

export type UsePipWindowOptions = {
  width?: number;
  height?: number;
};

export type UsePipWindowResult = {
  pipWindow: Window | null;
  open: () => Promise<void>;
  isSupported: boolean;
};

declare global {
  interface DocumentPictureInPictureOptions {
    width?: number;
    height?: number;
    disallowReturnToOpener?: boolean;
    preferInitialWindowPlacement?: boolean;
  }

  interface DocumentPictureInPicture {
    readonly window: Window | null;
    requestWindow(options?: DocumentPictureInPictureOptions): Promise<Window>;
  }

  interface Window {
    documentPictureInPicture?: DocumentPictureInPicture;
  }
}

const isPipSupported = (): boolean => typeof window !== 'undefined' && 'documentPictureInPicture' in window;

function copyStylesheets(targetDocument: Document) {
  for (const styleSheet of Array.from(document.styleSheets)) {
    try {
      const cssRules = Array.from(styleSheet.cssRules)
        .map((rule) => rule.cssText)
        .join('');
      const style = targetDocument.createElement('style');
      style.textContent = cssRules;
      targetDocument.head.appendChild(style);
    } catch {
      if (styleSheet.href) {
        const link = targetDocument.createElement('link');
        link.rel = 'stylesheet';
        link.href = styleSheet.href;
        if (styleSheet.type) link.type = styleSheet.type;
        if (styleSheet.media?.mediaText) link.media = styleSheet.media.mediaText;
        targetDocument.head.appendChild(link);
      }
    }
  }
}

function syncDocumentClasses(targetDocument: Document) {
  targetDocument.documentElement.className = document.documentElement.className;
  const colorScheme = document.documentElement.style.colorScheme;
  if (colorScheme) {
    targetDocument.documentElement.style.colorScheme = colorScheme;
  }
  targetDocument.documentElement.style.height = '100%';
  targetDocument.body.style.height = '100%';
  targetDocument.body.style.margin = '0';
  if (!targetDocument.head.querySelector('base')) {
    const base = targetDocument.createElement('base');
    base.href = document.baseURI;
    targetDocument.head.prepend(base);
  }
}

export const usePipWindow = ({ width = 340, height = 400 }: UsePipWindowOptions = {}): UsePipWindowResult => {
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const pipWindowRef = useRef<Window | null>(null);
  const isSupported = isPipSupported();

  const open = useCallback(async () => {
    const current = pipWindowRef.current;
    if (current && !current.closed) {
      current.focus();
      return;
    }
    if (!window.documentPictureInPicture) return;

    try {
      const nextWindow = await window.documentPictureInPicture.requestWindow({ width, height });
      copyStylesheets(nextWindow.document);
      syncDocumentClasses(nextWindow.document);
      nextWindow.addEventListener(
        'pagehide',
        () => {
          // A closed window's pagehide can land after its replacement opened.
          if (pipWindowRef.current !== nextWindow) return;
          pipWindowRef.current = null;
          setPipWindow(null);
        },
        { once: true }
      );
      pipWindowRef.current = nextWindow;
      setPipWindow(nextWindow);
    } catch {
      /* Document PiP request rejected */
    }
  }, [height, width]);

  useEffect(() => () => pipWindowRef.current?.close(), []);

  return { pipWindow, open, isSupported };
};
